import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createLogger } from "../../../../lib/logger";
import { useAppStore } from "../../../../store/useAppStore";
import { Deployment } from "../../../../types";

const log = createLogger("useProjectDeployments");

export interface UseProjectDeploymentsResult {
  deployments: Deployment[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProjectDeployments(
  projectId: string,
  selectedEnvironmentId?: string
): UseProjectDeploymentsResult {
  const { fetchDeployments } = useAppStore();
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirror of useProjectServices: set loading=true synchronously before paint
  // when transitioning from disabled ("") to an active projectId.
  const prevProjectIdRef = useRef(projectId);
  useLayoutEffect(() => {
    if (projectId && !prevProjectIdRef.current) {
      setLoading(true);
    }
    prevProjectIdRef.current = projectId;
  }, [projectId]);

  const fetchProjectDeployments = useCallback(async () => {
    if (!projectId) {
      setDeployments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedDeployments = await (fetchDeployments as any)(
        projectId,
        undefined,
        selectedEnvironmentId
      );

      // Sort deployments by creation date (newest first)
      const sortedDeployments = (fetchedDeployments || []).sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setDeployments(sortedDeployments);
    } catch (err) {
      log.error("Failed to fetch deployments", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch deployments"
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedEnvironmentId, fetchDeployments]);

  useEffect(() => {
    fetchProjectDeployments();
  }, [fetchProjectDeployments]);

  return {
    deployments,
    loading,
    error,
    refetch: fetchProjectDeployments,
  };
}
